import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronUp, Volume2, VolumeX, TrendingUp, Phone, Save, FileText, Brain, Download, Copy, X, Clock, User, MessageSquare } from 'lucide-react';
import "./emma.css"
import {
  convertBlobToBase64,
  ensureSingleValidAudioTrack,
  getAudioStream,
  getBrowserSupportedMimeType,
  EVIWebAudioPlayer,
  MimeType
} from 'hume';


// Data Store Service
class DataStoreService {
  async saveConversation(userId, data) {
    try {
      const stored = JSON.stringify(data);
      // In a real app, this would use a proper database
      // For demo, we'll use memory storage
      this.conversationData = stored;
    } catch (error) {
      console.error('Error saving conversation:', error);
    }
  }
  
  async getConversation(userId) {
    try {
      // In a real app, this would fetch from a database
      return this.conversationData ? JSON.parse(this.conversationData) : null;
    } catch (error) {
      console.error('Error getting conversation:', error);
      return null;
    }
  }
  
  async saveMemory(key, data) {
    try {
      const stored = JSON.stringify(data);
      // In a real app, this would use a proper database
      this.memoryData = this.memoryData || {};
      this.memoryData[key] = stored;
    } catch (error) {
      console.error('Error saving memory:', error);
    }
  }
  
  async getMemory(key) {
    try {
      if (this.memoryData && this.memoryData[key]) {
        return JSON.parse(this.memoryData[key]);
      }
      return null;
    } catch (error) {
      console.error('Error getting memory:', error);
      return null;
    }
  }
}


// Azure OpenAI Service (mock for demo)
// Azure OpenAI Service - Real API Implementation
class AzureOpenAIService {
  constructor(endpoint, apiKey, deployment) {
    this.endpoint = endpoint;
    this.apiKey = apiKey;
    this.deployment = deployment;
  }
  
  async generateResponse(prompt, maxTokens = 1000) {
    try {
      // Construct the API URL
      const apiUrl = `${this.endpoint}/openai/deployments/${this.deployment}/chat/completions?api-version=2023-12-01-preview`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': this.apiKey
        },
        body: JSON.stringify({
          messages: [
            {
              role: 'system',
              content: "You are a journaling assistant. Based on the conversation below, write a brief journal summary in the first person. Focus on the user's emotional journey or personal reflections."
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: maxTokens,
          temperature: 0.7,
          top_p: 0.95,
          frequency_penalty: 0,
          presence_penalty: 0
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Azure OpenAI API error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
      }

      const data = await response.json();
      return data.choices[0]?.message?.content || 'No response generated';
      
    } catch (error) {
      console.error('Azure OpenAI API error:', error);
      throw new Error(`Failed to generate response: ${error.message}`);
    }
  }
  
  async summarizeConversation(conversation) {
    if (!conversation || conversation.length === 0) {
      throw new Error('No conversation data provided');
    }
    
    // Format conversation for better readability
    const formattedConversation = conversation
      .filter(msg => msg.type !== 'system') // Exclude system messages
      .map(msg => {
        const timestamp = msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : '';
        const speaker = msg.type === 'user' ? 'User' : 'Assistant';
        return `${speaker} [${timestamp}]: ${msg.content}`;
      })
      .join('\n');
    
const prompt = `
Please analyze the following conversation between a user and an AI emotional companion.
*Never mention anything AI or companion during analysis.

Write a short, empathetic summary **as if it's a journal entry** that reflects only what the **user shared**. Do not mention the AI or what the AI said.

Focus on:
- What the user expressed (feelings, concerns, thoughts)
- Any emotional shifts or patterns
- Any meaningful themes that emerged
- Use a warm, human tone and speak directly to the user using "you" language
- Keep the tone natural, as if summarizing a moment of reflection

Avoid any formatting like markdown headers. The final output should feel like a personal, thoughtful reflection — 3 to 5 sentences max.

Conversation (${conversation.length} messages):
${formattedConversation}

Keep the voice grounded, simple, and kind — like something you'd read back to yourself in a private moment.
`;
    
    return await this.generateResponse(prompt, 1500);
  }
  
  async generateEmotionalInsights(conversationData) {
    if (!conversationData || conversationData.length === 0) {
      throw new Error('No conversation data provided for emotional insights');
    }
    
    // Extract emotional data and conversation context
    const emotionData = conversationData
      .filter(msg => msg.emotions && msg.emotions.length > 0)
      .map(msg => ({
        content: msg.content,
        emotions: msg.emotions,
        timestamp: msg.timestamp
      }));
    
    const conversationText = conversationData
      .filter(msg => msg.type !== 'system')
      .map(msg => `${msg.type}: ${msg.content}`)
      .join('\n');
    
const prompt = `
Analyze the following conversation and emotional data to generate a personal, human-style journal entry that helps the user reflect on their emotional state and communication patterns.

Structure the output like a journal entry, not a technical report.

Use the following format:

---

**Today’s Journal Entry**

Start with a brief description of the day’s emotional flow. Describe shifts in mood, energy, or tone that stood out.

Mention any highs or lows — moments where the user felt especially strong emotions (positive or negative).

Note any emotional patterns that repeated — recurring thoughts, reactions, or emotional triggers.

Close with a supportive reflection, like something the user might write to themselves at the end of the day. Keep the tone warm, self-aware, and constructive.

---

${emotionData.length > 0 ? `Conversation emotion signals:\n${JSON.stringify(emotionData, null, 2)}\n` : 'No specific emotion signals detected. Analyze based on tone and phrasing.\n'}

Full conversation:
${conversationText}

Avoid referencing systems, AI, or technical tools. Focus only on the user’s feelings, reflections, and personal growth.
`;

    return await this.generateResponse(prompt, 1500);
  }
  
  async generatePersonalSummary(conversationData) {
    if (!conversationData || conversationData.length === 0) {
      throw new Error('No conversation data provided for personal summary');
    }
    
    const conversationText = conversationData
      .filter(msg => msg.type !== 'system')
      .map(msg => `${msg.type}: ${msg.content}`)
      .join('\n');
    
const prompt = `
Based on this conversation, generate a personal summary in JSON format with the following structure:
{
  "narrative": "A brief personal narrative of what happened in the conversation",
  "themes": ["array", "of", "main", "themes"],
  "actions": ["array", "of", "actions", "or", "strategies", "discussed"],
  "tone": "description of the overall emotional tone progression"
}

Conversation:
${conversationText}

Write the narrative as if you're gently reflecting back to the user in a compassionate tone. Focus only on what the user expressed — their feelings, thoughts, and intentions. Avoid referencing AI, systems, or tools.

The output should feel warm and insightful, like a kind mirror showing the user's emotional journey.

Return only valid JSON — no extra explanation or formatting.
`;

    
    const response = await this.generateResponse(prompt, 800);
    
    try {
      // Try to parse the JSON response
      return JSON.parse(response);
    } catch (error) {
      // If JSON parsing fails, return a fallback structure
      console.error('Failed to parse personal summary JSON:', error);
      return {
        narrative: "The conversation covered important personal topics and emotional themes.",
        themes: ["personal reflection", "emotional wellbeing"],
        actions: ["continue self-reflection", "practice mindfulness"],
        tone: "thoughtful and introspective"
      };
    }
  }
}

// Export Service
const ExportService = {
  generateMarkdown: (conversation, summary, insights) => {
    return `# Voice Journal\n\n## Summary\n${summary}\n\n## Insights\n${insights}\n\n## Conversation\n${conversation.map(msg => `**${msg.type}**: ${msg.content}`).join('\n')}`;
  },
  
  downloadAsFile: (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
  
  copyToClipboard: async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }
}
const HumeEVIInterface = () => {
  // Hume EVI states
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [error, setError] = useState('');
  const [conversation, setConversation] = useState([]);
  const [currentEmotion, setCurrentEmotion] = useState('');
  const [isEviSpeaking, setIsEviSpeaking] = useState(false);
  
  // Enhanced AI services states
  const [conversationSummary, setConversationSummary] = useState('');
  const [emotionalInsights, setEmotionalInsights] = useState('');
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [userId] = useState('user_' + Date.now());
  
  // New resume chat states
  const [chatGroupId, setChatGroupId] = useState(null);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [isResumingChat, setIsResumingChat] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  
  // New summary queue states
  const [summaryQueue, setSummaryQueue] = useState([]);
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [lastActivity, setLastActivity] = useState(null);
  const [autoSummaryPending, setAutoSummaryPending] = useState(false);
  
  // Personal summary popup states
  const [showPersonalSummary, setShowPersonalSummary] = useState(false);
  const [personalSummary, setPersonalSummary] = useState('');
  const [summaryThemes, setSummaryThemes] = useState([]);
  const [summaryActions, setSummaryActions] = useState([]);
  const [summaryTone, setSummaryTone] = useState('');
  
  // UI states from your design
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showConversation, setShowConversation] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showJournalView, setShowJournalView] = useState(false);
  const [activeTab, setActiveTab] = useState('Listen');
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [watchingThreads, setWatchingThreads] = useState(0);
  const [message, setMessage] = useState('');
  const [response, setResponse] = useState('');
  const [showResponse, setShowResponse] = useState(false);
  const [pulseIntensity, setPulseIntensity] = useState(0);
  
  // Service instances
  const dataStoreService = useRef(new DataStoreService());
  const azureOpenAIService = useRef(null);
  
  // Session timeout and activity tracking
  const sessionTimeoutRef = useRef(null);
  const activityTimeoutRef = useRef(null);
  const SESSION_TIMEOUT = 1500; 
  const ACTIVITY_THRESHOLD = 2000;
  
  // Hume EVI refs
  const wsRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const playerRef = useRef(null);

  // Configuration
  // Configuration - replace with your actual values
 const HUME_API_KEY = process.env.REACT_APP_HUME_API_KEY;
  const CONFIG_ID = process.env.REACT_APP_HUME_CONFIG_ID;
  const AZURE_OPENAI_ENDPOINT = process.env.REACT_APP_AZURE_OPENAI_ENDPOINT;
  const AZURE_OPENAI_API_KEY = process.env.REACT_APP_AZURE_OPENAI_API_KEY;
  const AZURE_OPENAI_DEPLOYMENT = process.env.REACT_APP_AZURE_OPENAI_DEPLOYMENT;

  const EVI_WS_URL = 'wss://api.hume.ai/v0/evi/chat';

  // Display and animation states
  const [displayedSummary, setDisplayedSummary] = useState('');
  const [displayedInsights, setDisplayedInsights] = useState('');
  const [typingTimeouts, setTypingTimeouts] = useState([]);
  const [emmaLoadingPhase, setEmmaLoadingPhase] = useState('initial');
  const [emmaLoadingProgress, setEmmaLoadingProgress] = useState(0);
  const [journalRequested, setJournalRequested] = useState(false);
  const [takingNoteMessage, setTakingNoteMessage] = useState(false);
  const [journalButtonClicked, setJournalButtonClicked] = useState(false);

// Audio file URLs - replace with your actual file paths
const STARTUP_SOUND_URL = '/sound/calling2.mp3'; // Plays when connecting/initializing
const PRE_RESPONSE_SOUND_URL = '/sound/reply.mp3'; // Plays before EVI responds

// Enhanced state management for multiple audio types
const [startupSoundPlaying, setStartupSoundPlaying] = useState(false);
const [preResponseSoundPlaying, setPreResponseSoundPlaying] = useState(false);
const [isInitializing, setIsInitializing] = useState(false);

// Audio refs for different sound types
const startupAudioRef = useRef(null);
const preResponseAudioRef = useRef(null);
// Modified WebSocket connection with delayed startup sound and parallel initialization
const connectToEVI = async () => {
  try {
    setConnectionStatus('connecting');
    setIsInitializing(true); // Start showing initialization state
    setError('');

    if (!HUME_API_KEY || HUME_API_KEY === 'your_hume_api_key_here') {
      throw new Error('Please set your Hume API key in the configuration');
    }

    const wsUrl = `${EVI_WS_URL}?apiKey=${HUME_API_KEY}&config_id=${CONFIG_ID}`;
    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = async () => {
      setIsConnected(true);
      setConnectionStatus('connected');
      console.log('Connected to Hume EVI');

      setConversation(prev => [...prev, {
        type: 'system',
        content: 'Connected to Hume EVI. Initializing audio... 🎙️',
        timestamp: new Date()
      }]);
      
      // Start parallel initialization processes
      const audioPlayerPromise = (async () => {
        try {
          await playerRef.current.init();
          console.log('✅ Audio player initialized');
        } catch (err) {
          console.error('Failed to initialize audio player:', err);
        }
      })();
      
      // Start session immediately (parallel to audio setup)
      startSession();
      
      // Start startup sound with 1-second delay (parallel to other initialization)
      const startupSoundPromise = (async () => {
        try {
          console.log('🔊 Starting delayed startup sound sequence...');
          await playStartupSound(70); // 1 second delay
          console.log('🔊 Startup sound sequence completed');
        } catch (error) {
          console.error('Startup sound failed:', error);
        }
      })();
      
      // Wait for all initialization to complete
      try {
        await Promise.all([audioPlayerPromise, startupSoundPromise]);
        console.log('✅ All initialization completed');
        
        // Update UI to show ready state
        setConversation(prev => [...prev, {
          type: 'system',
          content: 'Ready to chat! 🎙️✨',
          timestamp: new Date()
        }]);
        
      } catch (error) {
        console.error('Some initialization failed:', error);
      } finally {
        setIsInitializing(false); // Stop showing initialization state
        
        // Start recording after everything is ready
        setTimeout(() => {
          startRecording();
        }, 400); // Small delay for smooth transition
      }
    };

    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      handleEVIMessage(data);
    };

    wsRef.current.onclose = () => {
      setIsConnected(false);
      setConnectionStatus('disconnected');
      setIsRecording(false);
      setIsEviSpeaking(false);
      setIsPlaying(false);
      setStartupSoundPlaying(false);
      setIsInitializing(false);
      endSession(true);
    };

    wsRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
      setError('WebSocket connection failed. Please check your API key or config ID.');
      setConnectionStatus('error');
      setIsInitializing(false);
    };

  } catch (err) {
    setError('Failed to connect to Hume EVI: ' + err.message);
    setConnectionStatus('error');
    setIsInitializing(false);
  }
};

// Flag to prevent multiple pre-response sounds per user turn
const preResponseSoundPlayedRef = useRef(false);

// Initialize startup audio (existing code)
const initializeStartupAudio = () => {
  if (!startupAudioRef.current) {
    startupAudioRef.current = new Audio(STARTUP_SOUND_URL);
    startupAudioRef.current.preload = 'auto';
    startupAudioRef.current.volume = 0.7;
    
    startupAudioRef.current.oncanplaythrough = () => {
      console.log('🔊 Startup sound loaded and ready');
    };
    
    startupAudioRef.current.onerror = (e) => {
      console.error('❌ Failed to load startup sound:', e);
    };
    
    startupAudioRef.current.onended = () => {
      setStartupSoundPlaying(false);
      console.log('🔊 Startup sound finished playing');
    };
  }
};

// NEW: Initialize pre-response audio
const initializePreResponseAudio = () => {
  if (!preResponseAudioRef.current) {
    preResponseAudioRef.current = new Audio(PRE_RESPONSE_SOUND_URL);
    preResponseAudioRef.current.preload = 'auto';
    preResponseAudioRef.current.volume = 0.6; // Slightly quieter than startup
    
    preResponseAudioRef.current.oncanplaythrough = () => {
      console.log('🔊 Pre-response sound loaded and ready');
    };
    
    preResponseAudioRef.current.onerror = (e) => {
      console.error('❌ Failed to load pre-response sound:', e);
    };
    
    preResponseAudioRef.current.onended = () => {
      setPreResponseSoundPlaying(false);
      console.log('🔊 Pre-response sound finished playing');
    };
  }
};

// Function to play startup sound with delay (existing)
const playStartupSound = async (delayMs = 600) => {
  if (!soundEnabled) return Promise.resolve();
  
  return new Promise((resolve, reject) => {
    console.log(`🔊 Waiting ${delayMs}ms before startup sound...`);
    
    setTimeout(() => {
      if (!startupAudioRef.current) {
        initializeStartupAudio();
      }
      
      if (startupAudioRef.current) {
        setStartupSoundPlaying(true);
        
        startupAudioRef.current.onended = () => {
          setStartupSoundPlaying(false);
          console.log('🔊 Startup sound finished playing');
          resolve();
        };
        
        startupAudioRef.current.onerror = (e) => {
          setStartupSoundPlaying(false);
          console.error('❌ Startup sound playback error:', e);
          reject(e);
        };
        
        const playPromise = startupAudioRef.current.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('🔊 Startup sound started playing');
            })
            .catch((error) => {
              setStartupSoundPlaying(false);
              console.error('❌ Startup sound play failed:', error);
              reject(error);
            });
        }
      } else {
        resolve();
      }
    }, delayMs);
  });
};

// NEW: Function to play pre-response sound (non-blocking, with optional short delay)
const playPreResponseSound = async (delayMs = 0) => {
  if (!soundEnabled) return Promise.resolve();
  
  return new Promise((resolve) => {
    console.log('🔊 Playing pre-response sound...');
    
    const playSound = () => {
      if (!preResponseAudioRef.current) {
        initializePreResponseAudio();
      }
      
      if (preResponseAudioRef.current) {
        setPreResponseSoundPlaying(true);
        
        preResponseAudioRef.current.onended = () => {
          setPreResponseSoundPlaying(false);
          console.log('🔊 Pre-response sound finished playing');
        };
        
        preResponseAudioRef.current.onerror = (e) => {
          setPreResponseSoundPlaying(false);
          console.error('❌ Pre-response sound playback error:', e);
        };
        
        // Reset audio to beginning in case it was played before
        preResponseAudioRef.current.currentTime = 0;
        
        const playPromise = preResponseAudioRef.current.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('🔊 Pre-response sound started playing');
              resolve(); // Resolve immediately when sound starts, not when it ends
            })
            .catch((error) => {
              setPreResponseSoundPlaying(false);
              console.error('❌ Pre-response sound play failed:', error);
              resolve(); // Still resolve to not block EVI
            });
        } else {
          resolve();
        }
      } else {
        resolve();
      }
    };
    
    if (delayMs > 0) {
      setTimeout(playSound, delayMs);
    } else {
      playSound();
    }
  });
};

// Enhanced message handling with pre-response sound
const handleEVIMessage = async (data) => {
  console.log('EVI Message:', data);
  updateActivity();
  
  switch (data.type) {
    case 'user_message':
      setMessage(data.message.content);
      
      // Reset the pre-response sound flag when user speaks
      preResponseSoundPlayedRef.current = false;
      
      // Check for journal command in user's speech
      if (detectJournalCommand(data.message.content)) {
        console.log('🎙️ Journal command detected via voice:', data.message.content);
        setJournalRequested(true);
        setJournalButtonClicked(true);
        setTakingNoteMessage(true);
        setSuccessMessage('📝 Journal will be created after call ends!');
        setTimeout(() => setSuccessMessage(''), 1000);
        setTimeout(() => {
          setTakingNoteMessage(false);
        }, 2000);
      }
      
      setConversation(prev => [...prev, {
        type: 'user',
        content: data.message.content,
        timestamp: new Date(),
        emotions: data.models?.prosody?.scores || []
      }]);
      
      if (data.models?.prosody?.scores && Array.isArray(data.models.prosody.scores) && data.models.prosody.scores.length > 0) {
        const topEmotion = data.models.prosody.scores.reduce((max, emotion) => 
          emotion.score > max.score ? emotion : max
        );
        setCurrentEmotion(topEmotion.name);
      }
      break;
      
    case 'assistant_message':
      // Play pre-response sound only once per user turn
      if (soundEnabled && !preResponseSoundPlayedRef.current) {
        preResponseSoundPlayedRef.current = true;
        playPreResponseSound().catch(error => {
          console.error('Pre-response sound failed:', error);
        });
      }
      
      setResponse(data.message.content);
      setShowResponse(true);
      setConversation(prev => [...prev, {
        type: 'assistant',
        content: data.message.content,
        timestamp: new Date()
      }]);
      setIsEviSpeaking(true);
      break;
      
    case 'audio_output':
      try {
        if (soundEnabled && playerRef.current) {
          await playerRef.current.enqueue(data);
          setIsPlaying(true);
        }
      } catch (err) {
        console.error('Audio playback error:', err);
      }
      break;
      
    case 'assistant_end':
      setIsEviSpeaking(false);
      setIsPlaying(false);
      setTimeout(() => {
        if (isConnected && !isRecording) {
          startRecording();
        }
      }, 1000);
      break;
      
    case 'user_interruption':
      setIsEviSpeaking(false);
      setIsPlaying(false);
      
      // Reset the pre-response sound flag on interruption too
      preResponseSoundPlayedRef.current = false;
      
      // Stop pre-response sound if it's playing during interruption
      if (preResponseAudioRef.current && preResponseSoundPlaying) {
        preResponseAudioRef.current.pause();
        preResponseAudioRef.current.currentTime = 0;
        setPreResponseSoundPlaying(false);
      }
      if (playerRef.current) {
        playerRef.current.stop();
      }
      break;
      
    default:
      console.log('Unknown message type:', data.type);
  }
  
  await saveConversationToDataStore();
};

// Enhanced toggleSound function to handle all audio types
const toggleSound = () => {
  const newSoundState = !soundEnabled;
  setSoundEnabled(newSoundState);
  
  if (!newSoundState) {
    // If turning sound off, stop all audio
    if (playerRef.current) {
      playerRef.current.stop();
      setIsPlaying(false);
    }
    if (startupAudioRef.current && startupSoundPlaying) {
      startupAudioRef.current.pause();
      startupAudioRef.current.currentTime = 0;
      setStartupSoundPlaying(false);
    }
    if (preResponseAudioRef.current && preResponseSoundPlaying) {
      preResponseAudioRef.current.pause();
      preResponseAudioRef.current.currentTime = 0;
      setPreResponseSoundPlaying(false);
    }
  }
};

// Enhanced getBuddyClasses to include pre-response sound state
const getBuddyClasses = () => {
  let classes = 'buddy-circle';
  if (isConnected) classes += ' connected';
  if (isRecording) classes += ' listening';
  if (isEviSpeaking) classes += ' speaking';
  if (autoSummaryPending) classes += ' processing';
  if (isInitializing) classes += ' initializing';
  if (startupSoundPlaying) classes += ' startup-sound';
  if (preResponseSoundPlaying) classes += ' thinking'; // New class for pre-response
  return classes;
};

// Enhanced useEffect initialization
useEffect(() => {
  // Initialize services
  if (AZURE_OPENAI_ENDPOINT && AZURE_OPENAI_API_KEY) {
    azureOpenAIService.current = new AzureOpenAIService(
      AZURE_OPENAI_ENDPOINT,
      AZURE_OPENAI_API_KEY,
      AZURE_OPENAI_DEPLOYMENT
    );
  }

  playerRef.current = new EVIWebAudioPlayer();
  initializeStartupAudio(); // Initialize startup sound
  initializePreResponseAudio(); // NEW: Initialize pre-response sound
  loadPreviousConversation();
  
  return () => {
    // Cleanup
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
    if (playerRef.current) {
      playerRef.current.stop();
    }
    // Cleanup all audio
    if (startupAudioRef.current) {
      startupAudioRef.current.pause();
      startupAudioRef.current.currentTime = 0;
    }
    if (preResponseAudioRef.current) {
      preResponseAudioRef.current.pause();
      preResponseAudioRef.current.currentTime = 0;
    }
  };
}, []);

// Enhanced handleStartOver to reset all audio states
const handleStartOver = async () => {
  // End current session before starting over
  if (sessionActive) {
    await endSession(false);
  }
  
  // Stop all audio if playing
  if (startupAudioRef.current && startupSoundPlaying) {
    startupAudioRef.current.pause();
    startupAudioRef.current.currentTime = 0;
    setStartupSoundPlaying(false);
  }
  
  if (preResponseAudioRef.current && preResponseSoundPlaying) {
    preResponseAudioRef.current.pause();
    preResponseAudioRef.current.currentTime = 0;
    setPreResponseSoundPlaying(false);
  }
  
  setIsInitializing(false);
  setConversation([]);
  setConversationSummary('');
  setEmotionalInsights('');
  setCurrentEmotion('');
  setMessage('');
  setResponse('');
  setShowResponse(false);
  
  // Stop audio playback
  if (playerRef.current) {
    playerRef.current.stop();
  }
  setIsPlaying(false);
  
  // Stop recording
  stopRecording();
};
// Enhanced useEffect initialization
useEffect(() => {
  // Initialize services
  if (AZURE_OPENAI_ENDPOINT && AZURE_OPENAI_API_KEY) {
    azureOpenAIService.current = new AzureOpenAIService(
      AZURE_OPENAI_ENDPOINT,
      AZURE_OPENAI_API_KEY,
      AZURE_OPENAI_DEPLOYMENT
    );
  }

  playerRef.current = new EVIWebAudioPlayer();
  initializeStartupAudio(); // Initialize startup sound
  initializePreResponseAudio(); // NEW: Initialize pre-response sound
  loadPreviousConversation();
  
  return () => {
    // Cleanup
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
    if (playerRef.current) {
      playerRef.current.stop();
    }
    // Cleanup all audio
    if (startupAudioRef.current) {
      startupAudioRef.current.pause();
      startupAudioRef.current.currentTime = 0;
    }
    if (preResponseAudioRef.current) {
      preResponseAudioRef.current.pause();
      preResponseAudioRef.current.currentTime = 0;
    }
  };
}, []);




const typeTextRealistic = (text, setDisplayedText) => {
  return new Promise((resolve) => {
    let index = 0;
    const timeouts = [];
    
    const getTypingDelay = (char) => {
      if (char === ' ') return 15;
      if (char === '.' || char === ',' || char === '!') return 30;
      if (char === '\n') return 40;
      return Math.random() * 15 + 8;
    };
    
    const typeNextChar = () => {
      if (index < text.length) {
        setDisplayedText(text.substring(0, index + 1));
        const delay = getTypingDelay(text[index]);
        index++;
        const timeout = setTimeout(typeNextChar, delay);
        timeouts.push(timeout);
      } else {
        resolve();
      }
    };
    
    typeNextChar();
    setTypingTimeouts(prev => [...prev, ...timeouts]);
  });
};
const generateSummary = async (showDropdown = false) => {
  if (!azureOpenAIService.current) {
    setError('Azure OpenAI not configured');
    return;
  }
  
  // Only show dropdown if explicitly requested (like when journal is clicked)
  if (showDropdown) {
    setShowSummary(true);
  }
  
  setIsGeneratingSummary(true);
  setError('');
  setDisplayedSummary('');
  setDisplayedInsights('');
  
  try {
    // Check if we have meaningful conversation
    if (conversation.length === 0) {
      setError('No conversation to summarize');
      return;
    }
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Phase 2: API calls (run in parallel with remaining delay)
    const summaryPromise = azureOpenAIService.current.summarizeConversation(conversation);
    const hasEmotionalData = conversation.some(msg => msg.emotions && msg.emotions.length > 0);
    const insightsPromise = hasEmotionalData ? 
      azureOpenAIService.current.generateEmotionalInsights(conversation) : 
      Promise.resolve('');
    
    const [summary, insights] = await Promise.all([
      summaryPromise,
      insightsPromise,
      new Promise(resolve => setTimeout(resolve, 1000))
    ]);
    
    // Phase 4: Final loading (additional 4 seconds)
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Set the results (this will trigger the typing animations)
    setConversationSummary(summary);
    if (insights) {
      setEmotionalInsights(insights);
    }
    
    await saveConversationToDataStore();
    
  } catch (error) {
    setError('Failed to generate summary: ' + error.message);
    console.error('Summary generation error:', error);
  } finally {
    setIsGeneratingSummary(false);
  }
};

// Additional helper function to ensure dropdown shows
const ensureDropdownShows = () => {
  if (journalRequested && !sessionActive) {
    setShowSummary(true);
    setJournalRequested(false);
  }
};

// Add this to your useEffect that monitors session state
useEffect(() => {
  if (!sessionActive && journalRequested) {
    // Session just ended and journal was requested
    ensureDropdownShows();
  }
}, [sessionActive, journalRequested]);

const generateConversationSummary = async (conversationData, showDropdown = false) => {
  if (!azureOpenAIService.current || conversationData.length === 0) {
    setError('No conversation to summarize or Azure OpenAI not configured');
    return;
  }
  
  if (showDropdown) {
    setShowSummary(true);
  }
  
  setIsGeneratingSummary(true);
  setError('');
  
  try {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Generate comprehensive conversation summary using real API
    const summary = await azureOpenAIService.current.summarizeConversation(conversationData);
    setConversationSummary(summary);
    
    // Generate emotional insights if emotional data is available
    const hasEmotionalData = conversationData.some(msg => msg.emotions && msg.emotions.length > 0);
    
    if (hasEmotionalData) {
      const insights = await azureOpenAIService.current.generateEmotionalInsights(conversationData);
      setEmotionalInsights(insights);
    }
    
    // Save updated conversation with summary
    await saveConversationToDataStore();
    
    // Show success message
    setSuccessMessage('✅ Conversation summary generated successfully!');
    setTimeout(() => setSuccessMessage(''), 1000);
    
  } catch (error) {
    setError('Failed to generate summary: ' + error.message);
    console.error('Summary generation error:', error);
  } finally {
    setIsGeneratingSummary(false);
  }
};


const endSession = async (autoEnd = false) => {
  if (!sessionActive) return;
  
  console.log('📋 Session ending...', autoEnd ? '(auto-end)' : '(manual)');
  setSessionActive(false);
  
  // Clear timers
  if (sessionTimeoutRef.current) {
    clearTimeout(sessionTimeoutRef.current);
  }
  if (activityTimeoutRef.current) {
    clearTimeout(activityTimeoutRef.current);
  }
  
  // Hide "taking note" message
  setTakingNoteMessage(false);
  
  // Generate summary if meaningful conversation exists
  if (conversation.length > 2) {
    // Only show summary dropdown if journal was requested
    if (journalRequested) {
      await generateSummary(true); // Pass true to show dropdown
      
      // Auto-switch to summary tab after a short delay
      setTimeout(() => {
        setActiveTab('Summary');
      }, 500);
      
      // Reset journal request state after showing dropdown
      setJournalRequested(false);
    } else {
      // Just generate summary without showing dropdown for auto-end
      await generateSummary(false); // Pass false to not show dropdown
    }
  } else if (journalRequested) {
    // Even if conversation is short, show dropdown if journal was requested
    setShowSummary(true);
    setJournalRequested(false);
    
    // Show message that there's not enough content
    setError('Not enough conversation content to generate a meaningful summary');
    setTimeout(() => setError(''), 1000);
  }
  
  await saveConversationToDataStore();
};


const handleJournalNavClick = () => {
  setActiveTab('journal');
  
  if (Array.isArray(conversation) && conversation.length > 0) {
    if (sessionActive) {
      // Mark journal as requested and show taking note message
      setJournalRequested(true);
      setTakingNoteMessage(true);
      
      // Show temporary success message
      setSuccessMessage('Summary will be ready when conversation ends');
      setTimeout(() => setSuccessMessage(''), 1000);
      
    } else {
      // Session not active - show dropdown and generate summary immediately
      setShowSummary(true);
      generateConversationSummary(conversation);
    }
  } else {
    // No conversation content
    if (sessionActive) {
      // Still mark as requested so dropdown shows when session ends
      setJournalRequested(true);
      setTakingNoteMessage(true);
      setSuccessMessage('📝 Taking note... Summary will be ready when conversation ends');
      setTimeout(() => setSuccessMessage(''), 1000);
    } else {
      setError('No conversation to summarize');
      setTimeout(() => setError(''), 1000);
    }
  }
};


// Session management
const startSession = () => {
  const now = new Date();
  setSessionActive(true);
  setSessionStartTime(now);
  setLastActivity(now);
  console.log('📅 Session started:', now.toLocaleTimeString());
  
  if (sessionTimeoutRef.current) {
    clearTimeout(sessionTimeoutRef.current);
  }
  
  sessionTimeoutRef.current = setTimeout(() => {
    endSession(true);
  }, SESSION_TIMEOUT);
};

const updateActivity = () => {
  const now = new Date();
  setLastActivity(now);
  
  if (!sessionActive) {
    startSession();
  } else {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    sessionTimeoutRef.current = setTimeout(() => {
      endSession(true);
    }, SESSION_TIMEOUT);
  }
};



// Audio capture using Hume SDK
const startAudioCapture = async (socket, timeSliceMs = 80) => {
  try {
    const mimeTypeResult = getBrowserSupportedMimeType();
    const mimeType = mimeTypeResult.success ? mimeTypeResult.mimeType : 'audio/webm';
    
    const micAudioStream = await getAudioStream();
    ensureSingleValidAudioTrack(micAudioStream);
    
    streamRef.current = micAudioStream;
    
    const recorder = new MediaRecorder(micAudioStream, { mimeType });
    
    recorder.ondataavailable = async (e) => {
      if (e.data.size > 0 && socket.readyState === WebSocket.OPEN) {
        try {
          const data = await convertBlobToBase64(e.data);
          socket.send(JSON.stringify({
            type: 'audio_input',
            data: data
          }));
        } catch (err) {
          console.error('Error sending audio data:', err);
        }
      }
    };
    
    recorder.onerror = (e) => {
      console.error("MediaRecorder error:", e);
      setError('Audio recording error: ' + e.error);
    };
    
    recorder.start(timeSliceMs);
    mediaRecorderRef.current = recorder;
    
    return recorder;
  } catch (err) {
    setError('Failed to start audio capture: ' + err.message);
    throw err;
  }
};

const detectJournalCommand = (text) => {
  if (!text || typeof text !== 'string') return false;
  
  const journalTriggers = [
    'journal',
    'journal this',
    'take note',
    'take notes',
    'save this',
    'record this',
    'note this',
    'write this down',
    'capture this',
    'log this',
    'summary',
    'summarize',
    'create journal',
    'make a note',
    'keep a record'
  ];
  
  const normalizedText = text.toLowerCase().trim();
  
  return journalTriggers.some(trigger => {
    // Check for exact matches or phrases that contain the trigger
    return normalizedText === trigger || 
           normalizedText.includes(trigger) ||
           normalizedText.startsWith(trigger) ||
           normalizedText.endsWith(trigger);
  });
};



// Recording controls
const startRecording = async () => {
  try {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected');
    }

    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }

    await startAudioCapture(wsRef.current);
    setIsRecording(true);
    updateActivity();
    
  } catch (err) {
    setError('Failed to start recording: ' + err.message);
    console.error('Recording error:', err);
  }
};

const stopRecording = () => {
  if (mediaRecorderRef.current && isRecording) {
    mediaRecorderRef.current.stop();
    setIsRecording(false);
  }
  
  if (streamRef.current) {
    streamRef.current.getTracks().forEach(track => track.stop());
  }
};

// Data persistence
const saveConversationToDataStore = async () => {
  if (!dataStoreService.current) return;
  
  try {
    const conversationData = {
      id: userId,
      conversation: conversation,
      summary: conversationSummary,
      insights: emotionalInsights,
      sessionStartTime: sessionStartTime,
      lastActivity: lastActivity,
      sessionActive: sessionActive,
      savedAt: new Date().toISOString()
    };
    
    await dataStoreService.current.saveConversation(userId, conversationData);
    console.log('💾 Conversation saved to data store');
  } catch (error) {
    console.error('Error saving conversation:', error);
  }
};

const loadPreviousConversation = async () => {
  if (!dataStoreService.current) return;
  
  try {
    const savedData = await dataStoreService.current.getConversation(userId);
    if (savedData) {
      setConversation(savedData.conversation || []);
      setConversationSummary(savedData.summary || '');
      setEmotionalInsights(savedData.insights || '');
      setSessionStartTime(savedData.sessionStartTime ? new Date(savedData.sessionStartTime) : null);
      setLastActivity(savedData.lastActivity ? new Date(savedData.lastActivity) : null);
      console.log('📂 Previous conversation loaded');
    }
  } catch (error) {
    console.error('Error loading previous conversation:', error);
  }
};

// Export functions
const exportConversationSummary = () => {
  if (!conversationSummary && !emotionalInsights) {
    setError('No summary available to export');
    return;
  }
  
  const exportContent = `# Voice Journal Session Summary\n\n` +
    `**Session Date:** ${new Date().toLocaleDateString()}\n` +
    `**Duration:** ${sessionStartTime ? `${sessionStartTime.toLocaleTimeString()} - ${new Date().toLocaleTimeString()}` : 'N/A'}\n` +
    `**Messages:** ${conversation.length}\n\n` +
    `${conversationSummary}\n\n` +
    `${emotionalInsights ? `---\n\n${emotionalInsights}` : ''}\n\n` +
    `---\n\n## Full Conversation\n\n` +
    conversation
      .filter(msg => msg.type !== 'system')
      .map(msg => {
        const timestamp = msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : '';
        const speaker = msg.type === 'user' ? 'You' : 'EVI';
        return `**${speaker}** ${timestamp}\n${msg.content}`;
      })
      .join('\n\n');
  
  ExportService.downloadAsFile(exportContent, `voice-journal-${new Date().toISOString().split('T')[0]}.md`, 'text/markdown');
  setSuccessMessage('✅ Summary exported!');
  setTimeout(() => setSuccessMessage(''), 1000);
};

// Effects
useEffect(() => {
  // Initialize services
  if (AZURE_OPENAI_ENDPOINT && AZURE_OPENAI_API_KEY) {
    azureOpenAIService.current = new AzureOpenAIService(
      AZURE_OPENAI_ENDPOINT,
      AZURE_OPENAI_API_KEY,
      AZURE_OPENAI_DEPLOYMENT
    );
  }

  playerRef.current = new EVIWebAudioPlayer();
  loadPreviousConversation();
  
  return () => {
    // Cleanup
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
    if (playerRef.current) {
      playerRef.current.stop();
    }
  };
}, []);

// Typing animations
useEffect(() => {
  if (conversationSummary && !isGeneratingSummary && displayedSummary !== conversationSummary) {
    typeTextRealistic(conversationSummary, setDisplayedSummary);
  }
}, [conversationSummary, isGeneratingSummary]);


// Cleanup typing timeouts
useEffect(() => {
  return () => {
    typingTimeouts.forEach(timeout => clearTimeout(timeout));
  };
}, [typingTimeouts]);

// Reset displayed text when summary panel is closed
useEffect(() => {
  if (!showSummary) {
    setDisplayedSummary('');
    setDisplayedInsights('');
    setEmmaLoadingPhase('initial');
    setEmmaLoadingProgress(0);
    typingTimeouts.forEach(timeout => clearTimeout(timeout));
    setTypingTimeouts([]);
  }
}, [showSummary]);


// Auto-save conversation periodically

    const copySummaryToClipboard = async () => {
    if (!conversationSummary) {
      setError('No summary available to copy');
      return;
    }
    
    const summaryText = `${conversationSummary}\n\n${emotionalInsights || ''}`;
    const success = await ExportService.copyToClipboard(summaryText);
    
    if (success) {
      setSuccessMessage('✅ Summary copied!');
      setTimeout(() => setSuccessMessage(''), 1000);
    } else {
      setError('Failed to copy');
    }
  };
  
  // Export functions
  const downloadJournal = () => {
    const markdown = ExportService.generateMarkdown(conversation, conversationSummary, emotionalInsights);
    const filename = `voice-journal-${new Date().toISOString().split('T')[0]}.md`;
    ExportService.downloadAsFile(markdown, filename, 'text/markdown');
    setSuccessMessage('Journal downloaded successfully!');
    setTimeout(() => setSuccessMessage(''), 1000);
  };


  const startListening = () => {
    if (!isConnected) {
      connectToEVI();
    } else {
      startRecording();
    }
    setActiveTab('Listen');
  };

  const stopListening = () => {
    stopRecording();
    // End session when user manually stops
    endSession(false);
  };


// Add this function to generate the circular dots
const generateCircularDots = () => {
  const dots = [];
  const centerX = 100; // Half of container width
  const centerY = 100; // Half of container height
  
  // Create concentric circles of dots
  const rings = [
   { radius: 20, dotSize: 7, dotCount: 8, isEdge: false },
    { radius: 35, dotSize: 5, dotCount: 12, isEdge: false },
    { radius: 50, dotSize: 4, dotCount: 16, isEdge: false },
    { radius: 65, dotSize: 3, dotCount: 20, isEdge: false },
    { radius: 80, dotSize: 2, dotCount: 24, isEdge: true },
    { radius: 90, dotSize: 1.5, dotCount: 28, isEdge: true }
  ];
  
  rings.forEach((ring, ringIndex) => {
    for (let i = 0; i < ring.dotCount; i++) {
      const angle = (i / ring.dotCount) * 2 * Math.PI;
      const x = centerX + ring.radius * Math.cos(angle);
      const y = centerY + ring.radius * Math.sin(angle);
      
      const waveDelay = (i / ring.dotCount) * 2 + (ringIndex * 0.3);
      const spreadDistance = ring.isEdge ? 
        Math.random() * 15 + 5 : // Edge dots spread more (5-20px)
        Math.random() * 8 + 2;   // Inner dots spread less (2-10px)
      
      dots.push(
        <div
          key={`${ringIndex}-${i}`}
          className={ring.isEdge ? 'circle-dot edge' : 'circle-dot-emma'}
          style={{
            width: ring.dotSize + 'px',
            height: ring.dotSize + 'px',
            left: (x - ring.dotSize / 2) + 'px',
            top: (y - ring.dotSize / 2) + 'px',
            animationDelay: waveDelay + 's',
            '--spread-distance': spreadDistance + 'px'
          }}
        />
      );
    }
  });
  
  // Add center dot
  dots.push(
    <div
      key="center"
      className="circle-dot-emma"
      style={{
        width: '10px',
        height: '10px',
        left: (centerX - 5) + 'px',
        top: (centerY - 5) + 'px',
        animationDelay: '0s',
        '--spread-distance': '3px'
      }}
    />
  );
  
  return dots;
};

return (
  <div className="hume-evi-interface">
    <div className="quiet-one-container">
      <div className="vertical-sidebar">
        <div className="sidebar-item active" title="Dashboard">
<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.81809 4.18179C8.99383 4.35753 8.99383 4.64245 8.81809 4.81819L6.13629 7.49999L8.81809 10.1818C8.99383 10.3575 8.99383 10.6424 8.81809 10.8182C8.64236 10.9939 8.35743 10.9939 8.1817 10.8182L5.1817 7.81819C5.09731 7.73379 5.0499 7.61933 5.0499 7.49999C5.0499 7.38064 5.09731 7.26618 5.1817 7.18179L8.1817 4.18179C8.35743 4.00605 8.64236 4.00605 8.81809 4.18179Z" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"></path></svg>
        </div>
        
        
        <div className="sidebar-item" title="Mood">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="none"
            stroke="#e34f10"
            strokeWidth="1.5"
            viewBox="0 0 24 24"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M8 14s1.5 2 4 2 4-2 4-2" />
            <line x1="9" y1="9" x2="9.01" y2="9" />
            <line x1="15" y1="9" x2="15.01" y2="9" />
          </svg>
        </div>

        <div className="sidebar-divider"></div>
        
        <div className="sidebar-item" title="Settings">
<svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 13v-2a1 1 0 0 0-1-1h-.757l-.707-1.707.535-.536a1 1 0 0 0 0-1.414l-1.414-1.414a1 1 0 0 0-1.414 0l-.536.535L14 4.757V4a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1v.757l-1.707.707-.536-.535a1 1 0 0 0-1.414 0L4.929 6.343a1 1 0 0 0 0 1.414l.536.536L4.757 10H4a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h.757l.707 1.707-.535.536a1 1 0 0 0 0 1.414l1.414 1.414a1 1 0 0 0 1.414 0l.536-.535 1.707.707V20a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-.757l1.707-.708.536.536a1 1 0 0 0 1.414 0l1.414-1.414a1 1 0 0 0 0-1.414l-.535-.536.707-1.707H20a1 1 0 0 0 1-1Z"/>
  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"/>
</svg>
      {isResumingChat && (
        <div className="resume-indicator">
          <div className="spinner">⟳</div>
          <span>Loading previous conversation...</span>
        </div>
      )}
        </div>
      </div>
      
      {/* Main Content Area */}
      <div className="main-content">
        {/* Top Status Bar */}
        <div className="top-status-bar">
          <div className="status-left">
            {currentEmotion && (
              <div className="emotion-indicator">
                <span className="emotion-text">{currentEmotion}</span>
              </div>
            )}
            {sessionActive && (
              <div className="session-status">
                <span>Session: {sessionStartTime ? Math.floor((Date.now() - sessionStartTime.getTime()) / 1000) : 0}s</span>
              </div>
            )}
          </div>
          
        </div>
<div className='ai-emma-buddy-container'>
  {/* Central AI Buddy */}
  <div 
    className={`ai-buddy ${isRecording && !isEviSpeaking ? 'listening' : ''}`} 
    onClick={isRecording ? stopListening : startListening}
  >
    <div className={getBuddyClasses()}>
      <div className="emma-wave-animation"></div>
      <div className="buddy-inner">
        <div className="buddy-core"></div>
      </div>
    </div>

    {/* Circular dots pattern for listening - only show when recording AND not speaking */}
    {isRecording && !isEviSpeaking && (
      <div className="dots-circle">
        {generateCircularDots()}
      </div>
    )}

    {/* Speaking dots (original) */}
    <div className={`dots-container ${isEviSpeaking ? 'show' : ''}`}>
      <div className="dot dot-1"></div>
      <div className="dot dot-2"></div>
      <div className="dot dot-3"></div>
      <div className="dot dot-4"></div>
    </div>

    {/* Status indicators */}
    {!isConnected && !startupSoundPlaying && (
      <div className="status-indicator">
        <div className="connection-dot disconnected"></div>
        <span>Ready to chat?</span>
      </div>
    )}

    {/* NEW: Calling indicator for Emma */}
    {startupSoundPlaying && (
      <div className="status-indicator calling emma-calling">
        <div className="connection-dot calling-dot emma-dot"></div>
        <span>Calling Emma</span>
      </div>
    )}
  </div>
</div>
        {/* Summary Panel */}
{showSummary && (conversationSummary || emotionalInsights || isGeneratingSummary) && (
    <div className="search-dropdown-overlay">
      <div className="search-dropdown">
        <div className="dropdown-header">
          <div className="dropdown-title">
            <div className="ai-summary-icon">
              {isGeneratingSummary ? (
                <div className="loading-spinner"></div>
              ) : (
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4.5 2C3.11929 2 2 3.11929 2 4.5C2 5.88072 3.11929 7 4.5 7C5.88072 7 7 5.88072 7 4.5C7 3.11929 5.88072 2 4.5 2ZM3 4.5C3 3.67157 3.67157 3 4.5 3C5.32843 3 6 3.67157 6 4.5C6 5.32843 5.32843 6 4.5 6C3.67157 6 3 5.32843 3 4.5ZM10.5 2C9.11929 2 8 3.11929 8 4.5C8 5.88072 9.11929 7 10.5 7C11.8807 7 13 5.88072 13 4.5C13 3.11929 11.8807 2 10.5 2ZM9 4.5C9 3.67157 9.67157 3 10.5 3C11.3284 3 12 3.67157 12 4.5C12 5.32843 11.3284 6 10.5 6C9.67157 6 9 5.32843 9 4.5ZM2 10.5C2 9.11929 3.11929 8 4.5 8C5.88072 8 7 9.11929 7 10.5C7 11.8807 5.88072 13 4.5 13C3.11929 13 2 11.8807 2 10.5ZM4.5 9C3.67157 9 3 9.67157 3 10.5C3 11.3284 3.67157 12 4.5 12C5.32843 12 6 11.3284 6 10.5C6 9.67157 5.32843 9 4.5 9ZM10.5 8C9.11929 8 8 9.11929 8 10.5C8 11.8807 9.11929 13 10.5 13C11.8807 13 13 11.8807 13 10.5C13 9.11929 11.8807 8 10.5 8ZM9 10.5C9 9.67157 9.67157 9 10.5 9C11.3284 9 12 9.67157 12 10.5C12 11.3284 11.3284 12 10.5 12C9.67157 12 9 11.3284 9 10.5Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                </svg>
              )}
            </div>
            <div className='emma-loading-dots'>
              <span className="ai-icon-title">
                {isGeneratingSummary ? (
                  <span>Preptune is summarizing</span>
                ) : (
                  "Your Journal"
                )}
              </span>
            </div>
          </div>
          <button 
            className="dropdown-close"
            onClick={() => setShowSummary(false)}
          >
            <ChevronUp size={16} />
          </button>
        </div>
        
        <div className="dropdown-content">
          {/* Loading State */}
          {isGeneratingSummary && (
            <div className="dropdown-item">
              <div className="item-text">
                <div className="item-title">Processing...</div>
                <div className="item-description">Analyzing your conversation and generating insights.</div>
              </div>
            </div>
          )}
          
          {/* Summary Section */}
          {conversationSummary && !isGeneratingSummary && (
            <div className="dropdown-item">

              <div className="item-text">
                <div className="item-title">Summary</div>
                <div className="item-description emma-typing-text" data-text={conversationSummary}>
                  {displayedSummary}
                </div>
              </div>
            </div>
          )}

          {/* Emotional Insights Section */}
          {emotionalInsights && !isGeneratingSummary && (
            <div className="dropdown-item">
              <div className="item-icon">
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.5 0.875C5.49797 0.875 3.875 2.49797 3.875 4.5C3.875 6.50203 5.49797 8.125 7.5 8.125C9.50203 8.125 11.125 6.50203 11.125 4.5C11.125 2.49797 9.50203 0.875 7.5 0.875ZM1 7.5C0.723858 7.5 0.5 7.72386 0.5 8C0.5 8.27614 0.723858 8.5 1 8.5H6.5V14C6.5 14.2761 6.72386 14.5 7 14.5H8C8.27614 14.5 8.5 14.2761 8.5 14V8.5H14C14.2761 8.5 14.5 8.27614 14.5 8C14.5 7.72386 14.2761 7.5 14 7.5H8.5V2C8.5 1.72386 8.27614 1.5 8 1.5H7C6.72386 1.5 6.5 1.72386 6.5 2V7.5H1Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
                </svg>
              </div>
              <div className="item-text">
                <div className="item-title">Emotional Insights</div>
                <div className="item-description emma-typing-text" data-text={emotionalInsights}>
                  {displayedInsights}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {!isGeneratingSummary && (conversationSummary || emotionalInsights) && (
            <div className="dropdown-item summary-actions">

              <div className="item-text">
                <div className="item-title">Actions</div>
                <div className="summary-buttons">
                  <button className="summary-btn primary" onClick={exportConversationSummary}>
                    <svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 11.917 9.724 16.5 19 7.5"/>
</svg>
 Auto saved
                  </button>
                  <button className="summary-btn secondary" onClick={copySummaryToClipboard}>
                    <svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
  <path fill-rule="evenodd" d="M18 3a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-1V9a4 4 0 0 0-4-4h-3a1.99 1.99 0 0 0-1 .267V5a2 2 0 0 1 2-2h7Z" clip-rule="evenodd"/>
  <path fill-rule="evenodd" d="M8 7.054V11H4.2a2 2 0 0 1 .281-.432l2.46-2.87A2 2 0 0 1 8 7.054ZM10 7v4a2 2 0 0 1-2 2H4v6a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3Z" clip-rule="evenodd"/>
</svg>
 Copy Summary
                  </button>
                  <button className="summary-btn secondary" onClick={downloadJournal}>
                    <svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 15v2a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-2m-8 1V4m0 12-4-4m4 4 4-4"/>
</svg>
 Download
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Empty state when no content and not generating */}
          {!isGeneratingSummary && !conversationSummary && !emotionalInsights && (
            <div className="dropdown-item">
              <div className="item-icon">
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.5 11C4.46243 11 2 8.53757 2 5.5C2 2.46243 4.46243 0 7.5 0C10.5376 0 13 2.46243 13 5.5C13 8.53757 10.5376 11 7.5 11ZM7.5 10C9.98528 10 12 7.98528 12 5.5C12 3.01472 9.98528 1 7.5 1C5.01472 1 3 3.01472 3 5.5C3 7.98528 5.01472 10 7.5 10ZM7.5 8.5C7.22386 8.5 7 8.27614 7 8V5.5C7 5.22386 7.22386 5 7.5 5C7.77614 5 8 5.22386 8 5.5V8C8 8.27614 7.77614 8.5 7.5 8.5ZM6.5 3.5C6.5 3.22386 6.72386 3 7 3H8C8.27614 3 8.5 3.22386 8.5 3.5C8.5 3.77614 8.27614 4 8 4H7C6.72386 4 6.5 3.77614 6.5 3.5Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
                </svg>
              </div>
              <div className="item-text">
                <div className="item-title">No Content</div>
                <div className="item-description">Start a conversation to generate your journal summary.</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
        )}
{/* Error/Success Messages */}
{error && (
  <div className="alert-notification error">
    <div className="alert-body">
      <span>{error}</span>
      <button className="alert-close-btn" onClick={() => setError('')}>×</button>
    </div>
  </div>
)}

{successMessage && (
  <div className="alert-notification success">
    <div className="alert-body">
      <span>{successMessage}</span>
      <button className="alert-close-btn" onClick={() => setSuccessMessage('')}>×</button>
    </div>
  </div>
)}
      </div>
      
      {/* Glass Navigation */}
<div className="glass-nav">
        <div 
          className={`glass-nav-item ${activeTab === 'Listen' ? 'active' : ''}`}
          onClick={isRecording ? stopListening : startListening}
        >
          <div className="ai-icon">
            {isGeneratingSummary ? (
              <div className="spinner">⟳</div>
            ) : isRecording ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5.693 16.013H7.31a1.685 1.685 0 0 0 1.685-1.684v-.645A1.684 1.684 0 0 1 10.679 12h2.647a1.686 1.686 0 0 1 1.686 1.686v.646c0 .446.178.875.494 1.19.316.317.693.495 1.14.495h1.685a1.556 1.556 0 0 0 1.597-1.016c.078-.214.107-.776.088-1.002.014-4.415-3.571-6.003-8-6.004-4.427 0-8.014 1.585-8.01 5.996-.02.227.009.79.087 1.003a1.558 1.558 0 0 0 1.6 1.02Z"/>
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.5 2C3.11929 2 2 3.11929 2 4.5C2 5.88072 3.11929 7 4.5 7C5.88072 7 7 5.88072 7 4.5C7 3.11929 5.88072 2 4.5 2ZM3 4.5C3 3.67157 3.67157 3 4.5 3C5.32843 3 6 3.67157 6 4.5C6 5.32843 5.32843 6 4.5 6C3.67157 6 3 5.32843 3 4.5ZM10.5 2C9.11929 2 8 3.11929 8 4.5C8 5.88072 9.11929 7 10.5 7C11.8807 7 13 5.88072 13 4.5C13 3.11929 11.8807 2 10.5 2ZM9 4.5C9 3.67157 9.67157 3 10.5 3C11.3284 3 12 3.67157 12 4.5C12 5.32843 11.3284 6 10.5 6C9.67157 6 9 5.32843 9 4.5ZM2 10.5C2 9.11929 3.11929 8 4.5 8C5.88072 8 7 9.11929 7 10.5C7 11.8807 5.88072 13 4.5 13C3.11929 13 2 11.8807 2 10.5ZM4.5 9C3.67157 9 3 9.67157 3 10.5C3 11.3284 3.67157 12 4.5 12C5.32843 12 6 11.3284 6 10.5C6 9.67157 5.32843 9 4.5 9ZM10.5 8C9.11929 8 8 9.11929 8 10.5C8 11.8807 9.11929 13 10.5 13C11.8807 13 13 11.8807 13 10.5C13 9.11929 11.8807 8 10.5 8ZM9 10.5C9 9.67157 9.67157 9 10.5 9C11.3284 9 12 9.67157 12 10.5C12 11.3284 11.3284 12 10.5 12C9.67157 12 9 11.3284 9 10.5Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
              </svg>
            )}
          </div>
          <span>{isRecording ? 'End Call' : 'Tap to Speak'}</span>
        </div>
        
        <div className="glass-nav-item" onClick={handleStartOver}>
          <div className="interrupt-icon">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.49991 0.877075C3.84222 0.877075 0.877075 3.84222 0.877075 7.49991C0.877075 11.1576 3.84222 14.1227 7.49991 14.1227C11.1576 14.1227 14.1227 11.1576 14.1227 7.49991C14.1227 3.84222 11.1576 0.877075 7.49991 0.877075ZM3.85768 3.15057C4.84311 2.32448 6.11342 1.82708 7.49991 1.82708C10.6329 1.82708 13.1727 4.36689 13.1727 7.49991C13.1727 8.88638 12.6753 10.1567 11.8492 11.1421L3.85768 3.15057ZM3.15057 3.85768C2.32448 4.84311 1.82708 6.11342 1.82708 7.49991C1.82708 10.6329 4.36689 13.1727 7.49991 13.1727C8.88638 13.1727 10.1567 12.6753 11.1421 11.8492L3.15057 3.85768Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
            </svg>
          </div>
          <span style={{ color: '#7c7c7c' }}>Clear</span>
        </div>
        

<div 
  className={`glass-nav-item ${activeTab === 'journal' ? 'active' : ''} ${takingNoteMessage ? 'taking-note' : ''} ${journalButtonClicked ? 'journal-requested' : ''}`}
  onClick={handleJournalNavClick}
>
  <div className="Journal-icon">
    {takingNoteMessage ? (
      <div className="note-taking-indicator">
        <svg width="17" height="17" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.875 7.5C9.875 8.81168 8.81168 9.875 7.5 9.875C6.18832 9.875 5.125 8.81168 5.125 7.5C5.125 6.18832 6.18832 5.125 7.5 5.125C8.81168 5.125 9.875 6.18832 9.875 7.5Z" fill="currentColor"/>
        </svg>
      </div>
    ) : isGeneratingSummary ? (
      <div className="spinner">⟳</div>
    ) : journalButtonClicked ? (
      <svg width="17" height="17" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11.4669 3.72684C11.7558 3.91574 11.8369 4.30308 11.648 4.59198L7.39799 11.092C7.29783 11.2452 7.13556 11.3467 6.95402 11.3699C6.77247 11.3931 6.58989 11.3355 6.45446 11.2124L3.70446 8.71241C3.44905 8.48022 3.43023 8.08494 3.66242 7.82953C3.89461 7.57412 4.28989 7.55529 4.5453 7.78749L6.75292 9.79441L10.6018 3.90792C10.7907 3.61902 11.178 3.53795 11.4669 3.72684Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
      </svg>
    ) : (
      <svg width="17" height="17" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 2.5C3 2.22386 3.22386 2 3.5 2H9.08579C9.21839 2 9.34557 2.05268 9.43934 2.14645L11.8536 4.56066C11.9473 4.65443 12 4.78161 12 4.91421V12.5C12 12.7761 11.7761 13 11.5 13H3.5C3.22386 13 3 12.7761 3 12.5V2.5ZM3.5 1C2.67157 1 2 1.67157 2 2.5V12.5C2 13.3284 2.67157 14 3.5 14H11.5C12.3284 14 13 13.3284 13 12.5V4.91421C13 4.51639 12.842 4.13486 12.5607 3.85355L10.1464 1.43934C9.86514 1.15804 9.48361 1 9.08579 1H3.5ZM4.5 4C4.22386 4 4 4.22386 4 4.5C4 4.77614 4.22386 5 4.5 5H7.5C7.77614 5 8 4.77614 8 4.5C8 4.22386 7.77614 4 7.5 4H4.5ZM4.5 7C4.22386 7 4 7.22386 4 7.5C4 7.77614 4.22386 8 4.5 8H10.5C10.7761 8 11 7.77614 11 7.5C11 7.22386 10.7761 7 10.5 7H4.5ZM4.5 10C4.22386 10 4 10.2239 4 10.5C4 10.7761 4.22386 11 4.5 11H10.5C10.7761 11 11 10.7761 11 10.5C11 10.2239 10.7761 10 10.5 10H4.5Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
      </svg>
    )}
  </div>
  <span style={{ color: takingNoteMessage ? '#ffa500' : '#33b300' }}>
    {takingNoteMessage ? 'Taking note' : 'Journal this'}
  </span>
  {watchingThreads > 0 && (
    <div className="notification-badge">{watchingThreads}</div>
  )}
</div>
        <div 
          className={`glass-nav-item ${activeTab === 'history' ? 'active' : ''}`}
                onClick={toggleSound}
                title={soundEnabled ? "Mute" : "Unmute"}
        >
          <div className="history-icon">
                {soundEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}

          </div>
          <span>Volume</span>
        </div>
      </div>
            <div className="controls">
              <button 

              >
              </button>
            </div>
{startupSoundPlaying ? (
  <div className="subtitle">Calling...</div>
) : isEviSpeaking ? (
  <div className="subtitle">Talk to interrupt</div>
) : isRecording ? (
  <div className="subtitle">Emma is Listening</div>
) : (
  <div className="subtitle">- Click "Tap to Speak" to Begin -</div>
)}

    </div>
  </div>
);
  };
export default HumeEVIInterface;